import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  IntakeDisposition,
  IntakeRecord,
  IntakeSource,
  ValidationResult,
  DuplicateMatch,
} from "@/data/leadIntegrityEngine";
import {
  DEFAULT_INTEGRITY_CONFIG,
  checkGeo,
  checkProjectFit,
  detectDuplicates,
  normalizeEmail,
  normalizeName,
  normalizePhone,
  normalizeZip,
  scoreIntake,
  validateEmail,
  validatePhone,
  validateRequiredFields,
} from "@/data/leadIntegrityEngine";
import type { Lead } from "@/data/pipelineTypes";

type DbLeadLike = Record<string, unknown>;
type DbValidationLike = Record<string, unknown>;

function safeString(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function safeArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function mapValidationStatus(status: string): ValidationResult["status"] {
  switch (status) {
    case "pass":
      return "valid";
    case "fail":
      return "invalid";
    case "warning":
      return "suspicious";
    case "error":
      return "suspicious";
    default:
      return "missing";
  }
}

function mapProviderField(providerKey: string): string {
  switch (providerKey) {
    case "outreach_compliance":
      return "phone";
    case "email":
      return "email";
    case "address":
      return "address";
    case "ip_location":
      return "geo";
    case "risk":
      return "fraud";
    case "trustedform":
      return "consent";
    default:
      return providerKey;
  }
}

function mapValidationResults(rows: DbValidationLike[], normalizedData: IntakeRecord["normalizedData"]): ValidationResult[] {
  if (!rows.length) {
    return [
      ...validateRequiredFields(normalizedData, DEFAULT_INTEGRITY_CONFIG.requiredFields),
      validatePhone(normalizedData.primaryPhone),
      validateEmail(normalizedData.primaryEmail),
    ];
  }

  const mapped = rows.map((row) => {
    const providerKey = safeString(row.provider_key);
    const decisionFragment =
      typeof row.decision_fragment === "object" && row.decision_fragment !== null
        ? (row.decision_fragment as Record<string, unknown>)
        : {};

    return {
      field: mapProviderField(providerKey),
      status: mapValidationStatus(safeString(row.status)),
      message:
        safeString(decisionFragment.reason) ||
        safeString(decisionFragment.result) ||
        providerKey.replace(/_/g, " ") ||
        "Validation result available",
    } satisfies ValidationResult;
  });

  return [
    ...validateRequiredFields(normalizedData, DEFAULT_INTEGRITY_CONFIG.requiredFields),
    ...mapped,
  ];
}

function toPipelineLead(row: DbLeadLike, sequenceNum?: number): Lead {
  const primaryName =
    safeString(row.primary_name) ||
    normalizeName(
      [safeString(row.first_name), safeString(row.last_name)].filter(Boolean).join(" "),
    );

  // Use provided sequence number for LEAD-XXX format, or fallback to ID
  const leadNum = sequenceNum || 1;

  return {
    id: safeString(row.id),
    displayReference: safeString(row.display_reference) || `LEAD-${String(leadNum).padStart(3, "0")}`,
    primaryName,
    secondaryName: safeString(row.secondary_name),
    primaryPhone: normalizePhone(safeString(row.primary_phone) || safeString(row.phone)),
    secondaryPhone: normalizePhone(safeString(row.secondary_phone)),
    primaryEmail: normalizeEmail(safeString(row.primary_email) || safeString(row.email)),
    secondaryEmail: normalizeEmail(safeString(row.secondary_email)),
    address: safeString(row.address) || safeString(row.address_line1),
    city: safeString(row.city),
    state: safeString(row.state),
    zip: normalizeZip(safeString(row.zip) || safeString(row.postal_code)),
    projectType: safeString(row.project_type) || "Other",
    leadSource: safeString(row.lead_source) || safeString(row.source_name),
    stage: (safeString(row.stage) || "Lead") as Lead["stage"],
    stageHistory: [],
    appointmentTime: safeString(row.appointment_time) || undefined,
    assignedRep: safeString(row.assigned_rep) || "Unassigned",
    ownershipHistory: [],
    activities: [],
    linkedQuoteId: safeString(row.linked_quote_id) || undefined,
    linkedJobId: safeString(row.linked_job_id) || undefined,
    totalEstimate: Number(row.total_estimate ?? 0) || 0,
    notes: safeString(row.notes),
    createdAt: safeString(row.created_at),
    updatedAt: safeString(row.updated_at),
    customerId: safeString(row.customer_id) || undefined,
    organizationId: safeString(row.organization_id) || undefined,
  };
}

function mapDecisionToDisposition(
  row: DbLeadLike,
  duplicates: DuplicateMatch[],
  validations: ValidationResult[],
): { disposition: IntakeDisposition; reason: string } {
  const decisionStatus = safeString(row.decision_status) || safeString(row.status);
  const reasons = safeArray(row.decision_reasons).map((reason) => safeString(reason)).filter(Boolean);
  const rejectionNotes = safeString(row.rejection_notes);
  const reason = rejectionNotes || reasons[0] || "Decision recorded";

  if (decisionStatus === "rejected") {
    return { disposition: "rejected", reason };
  }

  if (decisionStatus === "pending_review") {
    return {
      disposition: duplicates.some((match) => match.type === "exact_phone" || match.type === "exact_email")
        ? "duplicate_flagged"
        : "quarantined",
      reason,
    };
  }

  if (decisionStatus === "accepted") {
    const hasWarnings = validations.some((validation) =>
      validation.status === "suspicious" || validation.status === "missing",
    );

    return {
      disposition: hasWarnings ? "approved_with_warning" : "approved",
      reason: reason || (hasWarnings ? "Approved with data quality warnings" : "All validations passed"),
    };
  }

  return {
    disposition: "quarantined",
    reason: reason || "Awaiting manual review",
  };
}

function mapLeadToIntakeRecord(
  row: DbLeadLike,
  validationRows: DbValidationLike[],
  allLeads: Lead[],
  sequenceNum: number,
): IntakeRecord {
  const normalizedData: IntakeRecord["normalizedData"] = {
    primaryName:
      safeString(row.primary_name) ||
      normalizeName(
        [safeString(row.first_name), safeString(row.last_name)].filter(Boolean).join(" "),
      ),
    secondaryName: safeString(row.secondary_name),
    primaryPhone: normalizePhone(safeString(row.primary_phone) || safeString(row.phone)),
    secondaryPhone: normalizePhone(safeString(row.secondary_phone) || safeString(row.alternate_phone)),
    primaryEmail: normalizeEmail(safeString(row.primary_email) || safeString(row.email)),
    secondaryEmail: normalizeEmail(safeString(row.secondary_email)),
    address: safeString(row.address) || safeString(row.address_line1),
    city: normalizeName(safeString(row.city)),
    state: safeString(row.state).toUpperCase() || "FL",
    zip: normalizeZip(safeString(row.zip) || safeString(row.postal_code)),
    projectType: safeString(row.project_type),
    leadSource: safeString(row.lead_source) || safeString(row.source_name) || safeString(row.provider),
  };

  const siblingLeads = allLeads.filter((lead) => lead.id !== safeString(row.id));
  const duplicateMatches = detectDuplicates(normalizedData, siblingLeads, DEFAULT_INTEGRITY_CONFIG);
  const validationResults = mapValidationResults(validationRows, normalizedData);
  const geoResult = checkGeo(normalizedData.zip, normalizedData.city, DEFAULT_INTEGRITY_CONFIG);
  const projectFitResult = checkProjectFit(normalizedData.projectType, DEFAULT_INTEGRITY_CONFIG);
  const { quality, total, breakdown } = scoreIntake(
    validationResults,
    duplicateMatches,
    geoResult,
    projectFitResult,
    normalizedData.leadSource,
    DEFAULT_INTEGRITY_CONFIG,
  );
  const decision = mapDecisionToDisposition(row, duplicateMatches, validationResults);

  return {
    id: safeString(row.source_id) || safeString(row.id),
    rawPayload:
      typeof row.raw_payload === "object" && row.raw_payload !== null
        ? (row.raw_payload as Record<string, unknown>)
        : {},
    normalizedData,
    source: (safeString(row.provider).toLowerCase() === "manual" ? "manual" : "webhook") as IntakeSource,
    receivedAt: safeString(row.created_at),
    processedAt: safeString(row.last_validated_at) || safeString(row.updated_at) || safeString(row.created_at),
    validationResults,
    duplicateMatches,
    geoResult,
    projectFitResult,
    qualityScore: quality,
    scoreBreakdown: breakdown,
    totalScore: total,
    disposition: decision.disposition,
    dispositionReason: decision.reason,
    reviewedBy: undefined,
    reviewedAt: undefined,
    reviewNote: undefined,
    createdLeadId: safeString(row.id),
    deliveredToPipeline: decision.disposition === "approved" || decision.disposition === "approved_with_warning",
  };
}

export const LeadIntegrityService = {
  async getAll() {
    try {
      const organizationId = await getCurrentOrganizationId();

      const [{ data: leads, error: leadsError }, { data: validationRows, error: validationsError }] =
        await Promise.all([
          supabase
            .from("leads")
            .select("*")
            .eq("organization_id", organizationId)
            .order("created_at", { ascending: false }),
          supabase
            .from("lead_validation_results")
            .select("*")
            .eq("organization_id", organizationId),
        ]);

      if (leadsError) {
        return { data: null as IntakeRecord[] | null, error: leadsError };
      }

      const safeLeads = Array.isArray(leads) ? (leads as DbLeadLike[]) : [];
      const safeValidationRows = validationsError || !Array.isArray(validationRows)
        ? []
        : (validationRows as DbValidationLike[]);
      const validationByLeadId = new Map<string, DbValidationLike[]>();

      for (const row of safeValidationRows) {
        const leadId = safeString(row.lead_id);
        if (!leadId) continue;
        const existing = validationByLeadId.get(leadId) ?? [];
        existing.push(row);
        validationByLeadId.set(leadId, existing);
      }

       const pipelineLeads = safeLeads
        .map((row, idx) => toPipelineLead(row, idx + 1));
       const records = safeLeads.map((row, idx) =>
         mapLeadToIntakeRecord(row, validationByLeadId.get(safeString(row.id)) ?? [], pipelineLeads, idx + 1),
       );

      return { data: records, error: null };
    } catch (error) {
      return {
        data: null as IntakeRecord[] | null,
        error: error instanceof Error ? error : new Error("Failed to load lead integrity records"),
      };
    }
  },

  async updateReviewStatus(record: IntakeRecord, action: "approve" | "reject", reviewNote: string) {
    if (!record.createdLeadId) {
      return { data: null, error: new Error("Missing lead id for review update") };
    }

    const now = new Date().toISOString();
    const updates: Record<string, unknown> =
      action === "approve"
        ? {
            status: "accepted",
            decision_status: "accepted",
            validation_status: "validated",
            rejection_reason: null,
            rejection_notes: reviewNote || "Manually approved from Lead Integrity Dashboard",
            is_duplicate: false,
            matched_duplicate_by: null,
            accepted_at: now,
            rejected_at: null,
            stage: "Lead",
            updated_at: now,
          }
        : {
            status: "rejected",
            decision_status: "rejected",
            validation_status: "rejected",
            rejection_notes: reviewNote || record.dispositionReason,
            rejected_at: now,
            updated_at: now,
          };

    const { data, error } = await supabase
      .from("leads")
      .update(updates)
      .eq("id", record.createdLeadId)
      .select("*")
      .single();

    return { data, error };
  },
};
